import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import Dashboard from '@/components/Dashboard/Dashboard';
import PortfolioDetail from '@/components/PortfolioDetail/PortfolioDetail';
import AdminPanel from '@/components/AdminPanel/AdminPanel';
import ExposureAnalysis from '@/components/ExposureAnalysis/ExposureAnalysis';
import AlertsView from '@/components/AlertsView/AlertsView';
import CommentsPage from '@/components/Comments/CommentsPage';
import Login from '@/components/Login/Login';
import AuthCallback from '@/components/Login/AuthCallback';
import Unauthorized from '@/components/auth/Unauthorized';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import SidebarLayout from '@/components/Layout/SidebarLayout';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { AuthProvider } from '@/contexts/AuthContext';
import { CommentsProvider } from '@/contexts/CommentsContext';
import { msalConfig } from '@/config/msalConfig';
import '@/styles/App.css';
import { useAuth } from '@/contexts/AuthContext';
import LcFeedbackWidget from '@/components/common/LcFeedbackWidget';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

function AuthenticatedFeedbackWidget() {
  const { user } = useAuth();
  return <LcFeedbackWidget email={user?.email ?? ''} />;
}

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      dark: '#115293',
      light: '#42a5f5'
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff'
    },
    error: {
      main: '#f44336'
    },
    warning: {
      main: '#ff9800'
    },
    success: {
      main: '#4caf50'
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    }
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 8,
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
        }
      }
    }
  }
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <MsalProvider instance={msalInstance}>
          <AuthProvider>
            <AuthenticatedFeedbackWidget />
            <CommentsProvider>
              <Router>
              <div className="App">
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />

                  {/* Protected routes - wrapped with SidebarLayout */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <SidebarLayout>
                          <Dashboard />
                        </SidebarLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/portfolio/:portfolioId"
                    element={
                      <ProtectedRoute>
                        <SidebarLayout>
                          <PortfolioDetail />
                        </SidebarLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/alerts"
                    element={
                      <ProtectedRoute>
                        <SidebarLayout>
                          <AlertsView />
                        </SidebarLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/comments"
                    element={
                      <ProtectedRoute>
                        <SidebarLayout>
                          <CommentsPage />
                        </SidebarLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/exposure"
                    element={
                      <ProtectedRoute>
                        <SidebarLayout>
                          <ExposureAnalysis />
                        </SidebarLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SidebarLayout>
                          <AdminPanel />
                        </SidebarLayout>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </div>
            </Router>
          </CommentsProvider>
        </AuthProvider>
        </MsalProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
